// @flow
import { pick } from "lodash";
import { isString } from "voca";
import appConfig from "appConfig";
import { isValidColor } from "utils/colorUtils";
import createReducer from "common/reducerBase";
import { types } from "../actions";
import WidgetStylesRecord from "../data/WidgetStylesRecord";
import BuiltInThemes from "../data/BuiltInThemes";

const getValidPaletteParams = (paletteParams: Object) => {
	const record: Object = WidgetStylesRecord();
	const paletteDefaults = record.palette;
	const allowedPaletteParams = Object.keys(paletteDefaults);
	const validPaletteParams = pick(paletteParams, allowedPaletteParams);

	return Object.keys(validPaletteParams).reduce((params, name) => {
		params[name] = isValidColor(validPaletteParams[name]) ? validPaletteParams[name] : paletteDefaults[name];
		return params;
	}, {});
};

const getFontAllowedDomainsRgx = () => appConfig.customization.fontDomains
	.map((domain) => `//${domain}/`)
	.join("|");

const getValidFonts = (fonts: Object, previousFonts: Object) => {
	const allowedDomains = getFontAllowedDomainsRgx();
	const families = Object.keys(fonts);

	return families.reduce((res, name) => {
		const isStringEntry = fonts[name] && isString(fonts[name]);
		const link = fonts[name]
			? isStringEntry ? fonts[name] : fonts[name].url : null;

		if (!link || link.match(allowedDomains)) { // either link not provided or needs to be using approved domain
			if (fonts[name] && !isStringEntry && fonts[name].active) {
				res.fontFamily = name; // use the last active found
			}

			if (link && !~res.links.indexOf(link)) {
				res.links.push(link);
			}
		}

		return res;
	}, {
		fontFamily: previousFonts.fontFamily || families[0],
		links: previousFonts.links.asMutable(),
	});
};

const getThemePalette = (theme, state) => (BuiltInThemes[theme] ? BuiltInThemes[theme] : state.palette);

const getStateStyles = (state, payload) => {
	const newStyles = {
		palette: payload.palette ? getValidPaletteParams(payload.palette)
			: payload.theme ? getThemePalette(payload.theme, state) : state.palette,
		fonts: payload.fonts ? getValidFonts(payload.fonts, state.fonts) : state.fonts,
		frame: payload.frame ? { ...state.frame, ...payload.frame } : state.frame,
	};

	return state.merge(newStyles, { deep: true });
};

export default createReducer(WidgetStylesRecord(), {

	[types.SET_WIDGET_STYLES]: (state: Object, { payload }) => getStateStyles(state, payload),

	[types.SET_CUSTOM_IMAGES_TO_LOAD]: (state: Object, { payload }: { payload: { images: Object } }) => state.set("loadCustomImages", payload.images),

	[types.SET_CUSTOM_IMAGES_LOAD_RESULTS]: (state: Object, { payload }: { payload: { results: Object[] } }) => {
		const results = payload.results.reduce((res, img) => {
			res[img.key] = img;
			return res;
		}, {});

		const cleanedToLoad = {};

		Object.keys(state.loadCustomImages).forEach((key) => { // filter out from to-load all images that have results
			if (!results[key]) {
				cleanedToLoad[key] = state.loadCustomImages[key];
			}
		});

		return state.merge({
			loadCustomImages: cleanedToLoad,
			customImagesResults: results,
		});
	},
});
